import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MagiclinkService } from "../../service/third-party/magiclink.service";
import {
  Flex,
  Stack,
  Text,
  Button,
  Center,
  Image,
  Box,
} from "@chakra-ui/react";
import { FormInput } from "../../components/form/controls/FormInput";
import { update } from "../../store/slice/Token/token.slice";
import { useNavigate } from "react-router-dom";
import { colors } from "../../constants/design.constants";

export const MagicLinkLogin: React.FC<any> = () => {
  const magiclinkService = new MagiclinkService();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formState, setFormState] = useState({ email: "", password: "" });

  const onChange = (e: any) => {
    const { name, value } = e.target;

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    await magiclinkService
      .handleLoginWithEmailOTP(formState.email)
      .then((token) => {
        navigate(
          `/auth/login/redirect/${formState.email}/${formState.password}?did_token=${token}`
        );
      })
      .catch((err) => {
        alert(err);
        return;
      });
  };

  const logout = async () => {
    const magiclinkService = new MagiclinkService();
    await magiclinkService.logout();
    dispatch(update({}));
  };

  useEffect(() => {
    logout();
  }, []);

  return (
    <Flex minH="100vh" w="100vw" bg={colors.void} overflow="hidden">
      {/* Full-bleed visual plane */}
      <Box position="relative" h="100vh" flex="1" minW={0} overflow="hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
            filter: "saturate(0.55) brightness(0.45)",
          }}
        >
          <source src="/assets/homepage-animation.mp4" type="video/mp4" />
        </video>

        {/* Atmospheric overlays */}
        <Box
          position="absolute"
          inset={0}
          zIndex={2}
          background={`
            linear-gradient(105deg, ${colors.void}ee 0%, ${colors.void}88 45%, transparent 70%),
            linear-gradient(to top, ${colors.void}cc 0%, transparent 40%)
          `}
        />
        <Box
          position="absolute"
          inset={0}
          zIndex={2}
          opacity={0.4}
          backgroundImage={`
            linear-gradient(${colors.border}55 1px, transparent 1px),
            linear-gradient(90deg, ${colors.border}55 1px, transparent 1px)
          `}
          backgroundSize="64px 64px"
          pointerEvents="none"
        />

        <Flex
          position="absolute"
          zIndex={3}
          left={{ base: "24px", md: "48px", xl: "72px" }}
          bottom={{ base: "100px", md: "80px" }}
          right={{ base: "24px", lg: "40%" }}
          flexDir="column"
          maxW="560px"
          animation="fade-in 0.6s ease-out"
        >
          <Text
            fontSize="11px"
            fontWeight="500"
            letterSpacing="0.2em"
            textTransform="uppercase"
            color={colors.signal}
            mb="16px"
          >
            Sumlookup · Platform
          </Text>
          <Text
            fontWeight={500}
            fontSize={{ base: "32px", md: "44px", xl: "52px" }}
            lineHeight="1.1"
            letterSpacing="-0.03em"
            color={colors.textPrimary}
            mb="20px"
          >
            Lighthouse
          </Text>
          <Text
            fontWeight={400}
            fontSize="16px"
            lineHeight="1.55"
            color={colors.textSecondary}
            maxW="420px"
          >
            Operational visibility across your managed integrations.
            Monitor, trigger, and govern every run from one command surface.
          </Text>
        </Flex>

        <Image
          pos="absolute"
          left={{ base: "24px", md: "72px" }}
          bottom="48px"
          zIndex={3}
          src="/assets/sidebar/slu-branding.svg"
          alt="Sumlookup"
          width="160px"
          filter="brightness(1.15) grayscale(0.2)"
          opacity={0.85}
        />
      </Box>

      {/* Auth panel */}
      <Flex
        display={{ base: "none", lg: "flex" }}
        w="420px"
        flexShrink={0}
        bg={colors.base}
        borderLeft={`1px solid ${colors.border}`}
        px="48px"
        align="center"
        justify="center"
        position="relative"
      >
        <form onSubmit={onSubmit} style={{ width: "100%" }}>
          <Stack spacing={5} w="full">
            <Box mb="8px">
              <Text
                fontSize="10px"
                fontWeight="500"
                letterSpacing="0.16em"
                textTransform="uppercase"
                color={colors.textTertiary}
                mb="8px"
              >
                Secure Access
              </Text>
              <Text
                fontWeight="500"
                fontSize="24px"
                letterSpacing="-0.02em"
                color={colors.textPrimary}
              >
                Sign in
              </Text>
            </Box>
            <FormInput
              name="email"
              type="email"
              title="Email"
              required
              onChange={onChange}
              textColor={colors.textPrimary}
            />
            <FormInput
              name="password"
              title="Anaplan Token"
              required
              tooltipText="You can find this token in the module created in Anaplan by your SUMLOOKUP consultant."
              onChange={onChange}
              textColor={colors.textPrimary}
            />
            <Button
              type="submit"
              h="44px"
              mt="8px"
              bg={colors.signal}
              color={colors.void}
              borderRadius="2px"
              fontWeight="500"
              fontSize="13px"
              letterSpacing="0.08em"
              textTransform="uppercase"
              _hover={{
                bg: "#7AC8DE",
              }}
              _active={{ bg: colors.signalDim }}
            >
              Authenticate
            </Button>

            <Center>
              <Text fontSize="12px" color={colors.textTertiary} textAlign="center">
                Request your site admin to whitelist your email for access.
              </Text>
            </Center>
          </Stack>
        </form>
        <Box
          fontSize="11px"
          pos="absolute"
          bottom="20px"
          left="48px"
          right="48px"
          color={colors.textTertiary}
          letterSpacing="0.02em"
        >
          © {new Date().getFullYear()} SUMLOOKUP PTY LTD
        </Box>
      </Flex>

      {/* Mobile auth overlay */}
      <Flex
        display={{ base: "flex", lg: "none" }}
        position="fixed"
        inset={0}
        zIndex={10}
        align="flex-end"
        justify="center"
        pointerEvents="none"
      >
        <Box
          pointerEvents="auto"
          w="100%"
          bg={colors.elevated}
          borderTop={`1px solid ${colors.border}`}
          p="28px 24px 32px"
          maxH="70vh"
          overflowY="auto"
        >
          <form onSubmit={onSubmit}>
            <Stack spacing={4}>
              <Text fontWeight="500" fontSize="20px" color={colors.textPrimary}>
                Sign in
              </Text>
              <FormInput
                name="email"
                type="email"
                title="Email"
                required
                onChange={onChange}
                textColor={colors.textPrimary}
              />
              <FormInput
                name="password"
                title="Anaplan Token"
                required
                tooltipText="You can find this token in the module created in Anaplan by your SUMLOOKUP consultant."
                onChange={onChange}
                textColor={colors.textPrimary}
              />
              <Button
                type="submit"
                h="44px"
                bg={colors.signal}
                color={colors.void}
                borderRadius="2px"
                fontWeight="500"
                fontSize="13px"
                letterSpacing="0.08em"
                textTransform="uppercase"
                _hover={{ bg: "#7AC8DE" }}
              >
                Authenticate
              </Button>
            </Stack>
          </form>
        </Box>
      </Flex>
    </Flex>
  );
};
